import type { Rarity } from '@kenocyber/shared';

/**
 * The little boxed indicators every occupied inventory cell carries.
 *
 * They exist because a bare `1` in a corner is not information: it could be a
 * level, a quantity or a count of anything. Each indicator is therefore a
 * labelled box - `NV.1`, `×2`, `E` - in its own corner, with a near-black fill
 * and a border borrowed from the item's own rarity, so it stays legible over
 * bright art and dark art alike.
 *
 * Absolutely positioned by construction: a badge must never reserve layout
 * space, because the art claims 99% of the cell and nothing may shrink it.
 */

/** Where a badge sits. Distinct corners, so two badges never overlap. */
export type BadgeCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface CellBadgeProps {
  readonly corner: BadgeCorner;
  readonly children: string;
  /** Full-text meaning, since the badge itself is deliberately terse. */
  readonly title: string;
  readonly testId?: string;
  /** `strong` is the filled treatment the equipped marker uses. */
  readonly variant?: 'outline' | 'strong';
}

export function CellBadge({
  corner,
  children,
  title,
  testId,
  variant = 'outline',
}: CellBadgeProps) {
  return (
    <span
      className="inv-badge"
      data-corner={corner}
      data-variant={variant}
      data-testid={testId}
      title={title}
    >
      {children}
    </span>
  );
}

interface CellBadgesProps {
  /** The level to show. Always shown, including level 1. */
  readonly level: number;
  /**
   * How many items this cell stands for.
   *
   * One for a cell that is a single instance; the real count for a tab that
   * groups identical copies. Always shown, including `×1` - "how many" is a
   * question every cell should answer without the player counting cells.
   */
  readonly quantity: number;
  readonly equipped?: boolean;
  /** Prefix for the test ids, so a spec can address one specific cell. */
  readonly testIdSuffix: string;
  readonly rarity: Rarity;
}

export function CellBadges({ level, quantity, equipped, testIdSuffix }: CellBadgesProps) {
  return (
    <>
      {equipped ? (
        <CellBadge
          corner="top-left"
          variant="strong"
          title="Equipado"
          testId={`inv-equipped-${testIdSuffix}`}
        >
          E
        </CellBadge>
      ) : null}
      <CellBadge corner="bottom-left" title={`Nível ${level}`} testId={`inv-level-${testIdSuffix}`}>
        {`NV.${level}`}
      </CellBadge>
      <CellBadge
        corner="bottom-right"
        title={quantity === 1 ? '1 cópia' : `${quantity} cópias`}
        testId={`inv-quantity-${testIdSuffix}`}
      >
        {`×${quantity}`}
      </CellBadge>
    </>
  );
}
